import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Plus, X } from 'lucide-react';

interface TextareaArrayFieldProps {
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  helpText?: string;
  minItems?: number;
  maxItems?: number;
  required?: boolean;
}

export const TextareaArrayField: React.FC<TextareaArrayFieldProps> = ({
  label,
  value = [],
  onChange,
  placeholder = '',
  helpText,
  minItems = 1,
  maxItems = 10,
  required = false,
}) => {
  const handleAdd = () => {
    if (value.length < maxItems) {
      onChange([...value, '']);
    }
  };

  const handleRemove = (index: number) => {
    if (value.length > minItems) {
      const newValue = value.filter((_, i) => i !== index);
      onChange(newValue);
    }
  };

  const handleChange = (index: number, text: string) => {
    const newValue = [...value];
    newValue[index] = text;
    onChange(newValue);
  };

  // Initialize with minimum number of items if empty
  useEffect(() => {
    if (value.length === 0 && minItems > 0) {
      onChange(Array(minItems).fill(''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minItems]);

  return (
    <div className="space-y-3">
      <div>
        <Label>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
        {helpText && (
          <p className="text-sm text-muted-foreground mt-1">{helpText}</p>
        )}
      </div>

      <div className="space-y-3">
        {value.map((item, index) => (
          <div key={index} className="flex gap-2 items-start">
            <div className="flex-1">
              <Textarea
                value={item}
                onChange={(e) => handleChange(index, e.target.value)}
                placeholder={`${placeholder} ${index + 1}`}
                rows={3}
                className="resize-none"
              />
            </div>
            {value.length > minItems && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => handleRemove(index)}
                className="mt-1 min-h-[44px] min-w-[44px]"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
      </div>

      {value.length < maxItems && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAdd}
          className="w-full min-h-[44px]"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Another Clause
        </Button>
      )}
    </div>
  );
};
